import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowRight, Truck, Shield, Clock, Star, Phone, Mail, MapPin, Calculator, Ruler, Plus, X, AlertCircle, MessageCircle } from "lucide-react";
import { Helmet } from 'react-helmet-async';
import { usePricing, applyDiscount, formatPrice } from "@/lib/pricing";

const API = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

// Halı motifi SVG - basit ve etkili
const CarpetLogo = ({ className }) => (
  <svg className={className} viewBox="0 0 40 40" fill="none">
    <rect x="4" y="6" width="32" height="28" rx="3" fill="#F97316" />
    <rect x="8" y="10" width="24" height="20" rx="2" fill="white" />
    <rect x="12" y="14" width="16" height="12" rx="1" fill="#F97316" />
    <circle cx="20" cy="20" r="3" fill="white" />
  </svg>
);

// Halı adları (sabit)
const CARPET_NAMES = {
  makine: "Makine Halısı",
  shaggy: "Shaggy Halı",
  bambu: "Bambu Halı",
  viskon: "Viskon Halı",
  akrilik: "Akrilik Halı",
  kilim: "Kilim",
  el_dokuma: "El Dokuma",
  yun: "Yün Halı",
  isparta: "Isparta Halısı",
  hasir: "Hasır Halı",
  hereke: "Hereke Halısı",
  ladik: "Ladik Halısı",
  step: "Step Halı",
  usak: "Uşak Halısı",
  cin: "Çin Halısı",
  afgan: "Afgan Halısı",
  bunyan: "Bünyan Halı",
  iran: "İran Halısı",
  ipek: "İpek Halı",
  nepal: "Nepal Halısı",
  deri: "Deri Halı",
  patchwork: "Patchwork",
};

const LandingPage = () => {
  const [priceModalOpen, setPriceModalOpen] = useState(false);
  const [whatsappSettings, setWhatsappSettings] = useState({
    whatsapp_number: "902126990301",
    whatsapp_message: "Merhaba, halı yıkama hizmeti hakkında bilgi almak istiyorum."
  });
  const [carpetList, setCarpetList] = useState([
    { id: 1, carpet_type: "makine", width: "", length: "" }
  ]);
  
  // Fiyat ve indirim oranı tek kaynaktan (GET /api/pricing) gelir; yüklenene kadar
  // hiçbir varsayılan fiyat/indirim gösterilmez.
  const { prices: carpetPrices, discountRate, ready: pricesReady, error: pricesError, reload: reloadPricing } = usePricing({ useFallback: true });

  useEffect(() => {
    fetchWhatsAppSettings();
  }, []);

  const fetchWhatsAppSettings = async () => {
    try {
      const res = await axios.get(`${API}/api/public/settings`);
      setWhatsappSettings(res.data);
    } catch (error) {
      console.error("WhatsApp ayarları yüklenemedi:", error);
    }
  };

  // Dinamik CARPET_TYPES oluştur
  const CARPET_TYPES = Object.fromEntries(
    Object.entries(CARPET_NAMES).map(([key, name]) => [
      key,
      { name, price: carpetPrices?.[key] ?? null }
    ])
  );

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent(whatsappSettings.whatsapp_message);
    const url = `https://wa.me/${whatsappSettings.whatsapp_number}?text=${message}`;
    window.open(url, '_blank');
  };

  const addCarpet = () => {
    setCarpetList([...carpetList, { 
      id: Date.now(), 
      carpet_type: "makine", 
      width: "", 
      length: "" 
    }]);
  };

  const removeCarpet = (id) => {
    if (carpetList.length > 1) {
      setCarpetList(carpetList.filter(c => c.id !== id));
    }
  };

  const updateCarpet = (id, field, value) => {
    setCarpetList(carpetList.map(c => 
      c.id === id ? { ...c, [field]: value } : c
    ));
  };

  const calculateTotal = () => {
    let totalArea = 0;
    let totalPrice = 0;
    const details = [];
    const discountRateDecimal = (discountRate ?? 0) / 100;

    carpetList.forEach(carpet => {
      const unitPrice = carpetPrices?.[carpet.carpet_type];
      if (pricesReady && unitPrice !== undefined && carpet.width && carpet.length && CARPET_NAMES[carpet.carpet_type]) {
        const area = parseFloat(carpet.width) * parseFloat(carpet.length);
        const price = area * unitPrice;
        totalArea += area;
        totalPrice += price;
        details.push({
          type: CARPET_NAMES[carpet.carpet_type],
          dimensions: `${carpet.width}x${carpet.length}`,
          area: area,
          unitPrice: unitPrice,
          price: price
        });
      }
    });

    const discountAmount = totalPrice * discountRateDecimal;
    const finalPrice = totalPrice - discountAmount;

    return { totalArea, totalPrice, discountAmount, finalPrice, details, discountRate };
  };

  const priceInfo = calculateTotal();

  const handleGetStarted = () => {
    handleWhatsAppClick();
  };

  return (
    <div className="min-h-screen bg-white pb-16 md:pb-0">
      <Helmet>
        <title>Halıyol - İstanbul Halı Yıkama Hizmeti</title>
        <meta name="description" content="İstanbul'da profesyonel halı yıkama hizmeti. Beylikdüzü, Esenyurt, Avcılar ve Büyükçekmece'de kapıdan alım teslim, uygun fiyat garantisi." />
        <meta name="keywords" content="halı yıkama istanbul, halı temizleme, kapıdan halı yıkama, beylikdüzü halı yıkama, esenyurt halı yıkama, avcılar halı yıkama" />
        <link rel="canonical" href="https://www.haliyol.com/" />
        <meta property="og:title" content="Halıyol - İstanbul Halı Yıkama Hizmeti" />
        <meta property="og:description" content="İstanbul'da profesyonel halı yıkama hizmeti. Kapıdan alım teslim, uygun fiyat garantisi." />
        <meta property="og:url" content="https://www.haliyol.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.haliyol.com/images/CIRULOGO.png" />
      </Helmet>
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-orange-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <CarpetLogo className="w-10 h-10" />
              <span className="text-xl font-bold text-slate-800 tracking-tight">HALIYOL</span>
            </div>
            <div className="hidden md:flex items-center gap-6">
              <a href="#nasil-calisir" className="text-slate-600 hover:text-orange-500 transition-colors font-medium">Nasıl Çalışır</a>
              <a href="#hizmetler" className="text-slate-600 hover:text-orange-500 transition-colors font-medium">Fiyat Listesi</a>
              <Link to="/about" className="text-slate-600 hover:text-orange-500 transition-colors font-medium">Hakkımızda</Link>
              <Link to="/support" className="text-slate-600 hover:text-orange-500 transition-colors font-medium">Yardım & Destek</Link>
              <a href="#iletisim" className="text-slate-600 hover:text-orange-500 transition-colors font-medium">İletişim</a>
            </div>
            <div className="hidden sm:flex items-center gap-2 sm:gap-3">
              <a href="tel:02126990301">
                <Button variant="ghost" className="text-slate-600 text-sm sm:text-base px-2 sm:px-4">
                  <Phone className="w-4 h-4 mr-2" />
                  0212 699 03 01
                </Button>
              </a>
              <Button
                onClick={handleWhatsAppClick}
                className="bg-green-500 hover:bg-green-600 text-white rounded-full px-4 sm:px-6 text-sm sm:text-base"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                WhatsApp
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-28 pb-20 px-4 bg-gradient-to-br from-orange-50 via-white to-orange-50 relative overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6 animate-fade-in">
              <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-4 py-2 rounded-full text-sm font-medium">
                <CarpetLogo className="w-5 h-5" />
                İstanbul'un Güvenilir Halı Yıkama Markası
              </div>
              <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold text-slate-800 tracking-tight leading-tight">
                Halınız <span className="text-orange-500">Tertemiz</span>,<br />
                Eviniz Pırıl Pırıl
              </h1>
              <p className="text-lg md:text-xl text-slate-600 max-w-xl leading-relaxed">
                Yılların verdiği tecrübeyle halınızı adresinizden alıyor,
                özenle yıkayıp tertemiz halde kapınıza kadar teslim ediyoruz.
              </p>
              <div className="flex flex-col sm:flex-row flex-wrap gap-4">
                <Button 
                  onClick={handleGetStarted}
                  className="bg-orange-500 hover:bg-orange-600 text-white rounded-full px-8 py-6 text-lg font-semibold flex items-center justify-center gap-2 transition-all duration-300 hover:scale-105 w-full sm:w-auto"
                  data-testid="hero-cta-btn"
                >
                  WhatsApp ile Talep Oluştur
                  <ArrowRight className="w-5 h-5" />
                </Button>
                <Button 
                  variant="outline" 
                  className="rounded-full px-8 py-6 text-lg font-semibold border-orange-300 hover:bg-orange-50 text-orange-600 flex items-center justify-center gap-2 transition-all duration-300 w-full sm:w-auto"
                  onClick={() => setPriceModalOpen(true)}
                  data-testid="hero-price-calc-btn"
                >
                  <Calculator className="w-5 h-5" />
                  Fiyat Hesapla
                </Button>
              </div>
              <a
                href="tel:02126990301"
                className="flex items-center justify-center gap-2 w-full sm:w-auto rounded-full px-8 py-6 text-lg font-semibold border-2 border-orange-500 text-orange-600 hover:bg-orange-50 transition-all duration-300"
                data-testid="hero-phone-btn"
              >
                <Phone className="w-5 h-5" />
                0212 699 03 01
              </a>
              <div className="flex items-center gap-8 pt-2">
                <div className="flex items-center gap-2">
                  <div className="flex -space-x-2">
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 border-2 border-white" />
                    ))}
                  </div>
                  <span className="text-slate-600 text-sm">500+ Mutlu Müşteri</span>
                </div>
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star key={i} className="w-5 h-5 text-orange-400 fill-orange-400" />
                  ))}
                  <span className="text-slate-600 text-sm ml-2">4.7/5</span>
                </div>
              </div>
            </div>
            <div className="relative animate-slide-up">
              <div className="relative z-10">
                <img 
                  src="https://images.unsplash.com/photo-1600166898405-da9535204843?w=800" 
                  alt="Renkli halılar"
                  className="rounded-3xl shadow-2xl w-full object-cover aspect-[4/3]"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 w-48 h-48 bg-orange-500/20 rounded-3xl -z-10" />
              <div className="absolute -top-6 -right-6 w-32 h-32 bg-orange-300/30 rounded-3xl -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="nasil-calisir" className="py-24 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-slate-800 mb-4">Nasıl Çalışır?</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Sadece 3 adımda halınızı profesyonel ellere teslim edin
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: <Phone className="w-8 h-8" />,
                title: "Talep Oluşturun",
                description: "İster bizi arayın, ister online sipariş oluşturun, isterseniz de WhatsApp üzerinden iletişime geçin. Size en uygun yöntemi tercih edin.",
                step: "01"
              },
              {
                icon: <Truck className="w-8 h-8" />,
                title: "Halınızı Teslim Edin",
                description: "Bölgenizdeki firmamız halınızı adresinizden alır.",
                step: "02"
              },
              {
                icon: <CarpetLogo className="w-8 h-8" />,
                title: "Tertemiz Teslim",
                description: "Profesyonel yıkama sonrası halınız kapınıza gelir.",
                step: "03"
              }
            ].map((item, index) => (
              <div 
                key={index} 
                className="relative bg-orange-50 rounded-3xl p-8 card-hover group"
                data-testid={`step-card-${index + 1}`}
              >
                <div className="absolute top-6 right-6 text-6xl font-bold text-orange-200 group-hover:text-orange-300 transition-colors">
                  {item.step}
                </div>
                <div className="w-16 h-16 bg-orange-500 rounded-2xl flex items-center justify-center text-white mb-6">
                  {item.icon}
                </div>
                <h3 className="text-xl font-bold text-slate-800 mb-3">{item.title}</h3>
                <p className="text-slate-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="hizmetler" className="py-24 px-4 bg-orange-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-bold text-slate-800 mb-4">Halı Türleri & Fiyatlar</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-6">
              Her türlü halı için profesyonel yıkama hizmeti
            </p>
            <Button 
              onClick={() => setPriceModalOpen(true)}
              className="bg-orange-500 hover:bg-orange-600 text-white rounded-full px-8 py-4"
              data-testid="services-price-calc-btn"
            >
              <Calculator className="w-5 h-5 mr-2" />
              Fiyat Hesapla
            </Button>
            {pricesError && (
              <p className="mt-4 text-sm text-red-600" data-testid="pricing-error">
                Fiyatlar şu an yüklenemedi.{" "}
                <button type="button" onClick={reloadPricing} className="underline font-semibold">Tekrar dene</button>
              </p>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            
            {[
              { type: "Makine Halısı", key: "makine"},
              { type: "Shaggy Halı", key: "shaggy"},
              { type: "Bambu Halı", key: "bambu"},
              { type: "Viskon Halı", key: "viskon"},
              { type: "Akrilik Halı", key: "akrilik"},
              { type: "Kilim", key: "kilim"},
              { type: "El Dokuma", key: "el_dokuma"},
              { type: "Yün Halı", key: "yun"},
              { type: "Isparta Halısı", key: "isparta"},
              { type: "Hasır Halı", key: "hasir"},
              { type: "Hereke Halısı", key: "hereke"},
              { type: "Ladik Halısı", key: "ladik"},          
              { type: "Step Halı", key: "step"},
              { type: "Uşak Halısı", key: "usak"},
              { type: "Çin Halısı", key: "cin"},
              { type: "Afgan Halısı", key: "afgan"},
              { type: "Bünyan Halı", key: "bunyan"},
              { type: "İran Halısı", key: "iran"},
              { type: "İpek Halı", key: "ipek"},
              { type: "Nepal Halısı", key: "nepal"},
              { type: "Deri Halı", key: "deri"},
              { type: "Patchwork", key: "patchwork"},

            ].map((item, index) => {
              const basePrice = CARPET_TYPES[item.key]?.price ?? null;
              return (
              <div 
                key={index}
                className="bg-white rounded-2xl p-4 border border-orange-200 card-hover"
                data-testid={`price-card-${index + 1}`}
              >
                {item.type === "Makine Halısı" || item.type === "Shaggy Halı" || item.type === "Bambu Halı" || item.type === "Viskon Halı" || item.type === "Akrilik Halı" ||
                 item.type === "Kilim" || item.type === "El Dokuma" || item.type === "Yün Halı" || item.type === "Isparta Halısı" || item.type === "Hasır Halı" ||
                 item.type === "Hereke Halısı" || item.type === "Ladik Halısı" || item.type === "Step Halı" || item.type === "Uşak Halısı" || item.type === "Çin Halısı" ||
                 item.type === "Afgan Halısı" || item.type === "Bünyan Halı" || item.type === "İran Halısı" || item.type === "İpek Halı" || item.type === "Nepal Halısı" ||
                 item.type === "Deri Halı" || item.type === "Patchwork" ? ( 
                  <div className="flex items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-bold text-slate-800 mb-1 leading-tight">{item.type}</h3>
                      <p className="text-sm text-slate-500 mb-1">{item.desc}</p>
                      <div className="flex items-baseline gap-1 mb-0.5">
                        <span className="text-xs line-through text-slate-400">{pricesReady && basePrice !== null ? formatPrice(basePrice) : "—"} ₺/m²</span>
                      </div>
                      <div className="flex items-center gap-1 flex-wrap">
                        <span className="text-2xl font-bold text-orange-500">{pricesReady && basePrice !== null ? formatPrice(applyDiscount(basePrice, discountRate)) : "—"}</span>
                        <span className="text-slate-500 text-sm">₺/m²</span>
                        {pricesReady && <span className="text-xs bg-orange-100 text-orange-600 font-semibold px-1.5 py-0.5 rounded-full">%{discountRate} İndirim</span>}
                      </div>
                    </div>
                    <div className="w-24 h-30 rounded-sm overflow-hidden flex-shrink-0">
                      <img 

                        src={item.type === "Patchwork"
                          ? "/images/Patchwork.jpg"
                          : item.type === "Deri Halı"
                          ? "/images/Deri Halı.jpg"
                          : item.type === "Nepal Halısı"
                          ? "/images/Nepal Halısı.jpg"
                          : item.type === "İpek Halı"
                          ? "/images/İpek Halı.jpg"
                          : item.type === "İran Halısı"
                          ? "/images/İran Halısı.jpg"
                          : item.type === "Bünyan Halı"
                          ? "/images/Bünyan Halı.jpg"
                          : item.type === "Afgan Halısı"
                          ? "/images/Afgan Halısı.jpg"
                          : item.type === "Çin Halısı"
                          ? "/images/Çin Halısı.jpg"
                          : item.type === "Uşak Halısı"
                          ? "/images/Uşak Halısı.jpg"
                          : item.type === "Step Halı"
                          ? "/images/Step Halı.jpg"
                          : item.type === "Ladik Halısı"
                          ? "/images/Ladik Halısı.jpg"
                          : item.type === "Hereke Halısı"
                          ? "/images/Hereke Halısı.jpg"
                          : item.type === "Hasır Halı"
                          ? "/images/Hasır Halı.jpg"
                          : item.type === "Isparta Halısı"
                          ? "/images/Isparta Halısı.jpg"
                          : item.type === "Yün Halı"
                          ? "/images/Yün Halı.jpg"
                          : item.type === "El Dokuma"
                          ? "/images/El Dokuma.jpg"
                          : item.type === "Kilim"
                          ? "/images/Kilim.jpg"
                          : item.type === "Akrilik Halı"
                          ? "/images/Akrilik Halı.jpg"
                          : item.type === "Viskon Halı"
                          ? "/images/Viskon Halı.jpg"
                          : item.type === "Bambu Halı"
                          ? "/images/Bambu Halı.jpg"
                          : item.type === "Shaggy Halı"
                          ? "/images/Shaggy Halı.jpg"
                          : item.type === "Makine Halısı"
                          ? "/images/Makine Halısı.jpg"
                          : ""
                        }
                        alt={item.type}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                  </div>
                ) : (
                  <>
                    <h3 className="text-base font-bold text-slate-800 mb-1 leading-tight">{item.type}</h3>
                    <p className="text-sm text-slate-500 mb-1">{item.desc}</p>
                    <div className="flex items-baseline gap-1 mb-0.5">
                      <span className="text-xs line-through text-slate-400">{pricesReady && basePrice !== null ? formatPrice(basePrice) : "—"} ₺/m²</span>
                    </div>
                    <div className="flex items-center gap-1 flex-wrap">
                      <span className="text-2xl font-bold text-orange-500">{pricesReady && basePrice !== null ? formatPrice(applyDiscount(basePrice, discountRate)) : "—"}</span>
                      <span className="text-slate-500 text-sm">₺/m²</span>
                      {pricesReady && <span className="text-xs bg-orange-100 text-orange-600 font-semibold px-1.5 py-0.5 rounded-full">%{discountRate} İndirim</span>}
                    </div>
                  </>
                )}
              </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Trust Section - Neden HALIYOL */}
      <section className="py-24 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-1">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">
                Neden <span className="text-orange-500">HALIYOL</span>?
              </h2>
              <p className="text-slate-600 mb-8">
                Güvenilir hizmet, şeffaf fiyatlandırma ve müşteri memnuniyeti odaklı yaklaşım.
              </p>
              <img 
                src="https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=600"
                alt="Modern oturma odası halı"
                className="rounded-2xl"
              />
            </div>
            <div className="lg:col-span-2 grid sm:grid-cols-2 gap-6">
              {[
                { icon: <Shield className="w-6 h-6" />, title: "Çift Aşamalı Yıkama", desc: "İlk yıkamada tüm kir ve lekeler temizlenir. İkinci yıkamada özel dezenfektanlar ile bakterilerden arındırılır ve hijyen sağlanır." },
                { icon: <Clock className="w-6 h-6" />, title: "Hızlı Teslimat", desc: "Ortalama 3 gün içinde tertemiz halınız kapınızda." },
                { icon: <Star className="w-6 h-6" />, title: "Kalite Garantisi", desc: "Profesyonel ekipman ve uzman kadro ile hizmet." },
                { icon: <Truck className="w-6 h-6" />, title: "Ücretsiz Alım", desc: "Halınızı ücretsiz olarak adresinizden alıyoruz." },
                { icon: <Shield className="w-6 h-6" />, title: "Halıyol Sigortası", desc: "Tüm halılarınız Halıyol sigortası kapsamında korunmaktadır. Olası hasarlara karşı güvence altındasınız." }
              ].map((item, index) => (
                <div 
                  key={index}
                  className="bg-orange-50 rounded-2xl p-6 card-hover"
                  data-testid={`feature-card-${index + 1}`}
                >
                  <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center text-orange-500 mb-4">
                    {item.icon}
                  </div>
                  <h3 className="text-lg font-bold text-slate-800 mb-2">{item.title}</h3>
                  <p className="text-slate-600 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Price Calculator Modal */}
      <Dialog open={priceModalOpen} onOpenChange={setPriceModalOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Calculator className="w-5 h-5 text-orange-500" />
              Fiyat Hesaplama
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {/* Carpet List */}
            {carpetList.map((carpet, index) => (
              <div key={carpet.id} className="bg-orange-50 rounded-xl p-4 relative">
                {carpetList.length > 1 && (
                  <button 
                    onClick={() => removeCarpet(carpet.id)}
                    className="absolute top-2 right-2 text-slate-400 hover:text-red-500"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
                <div className="text-sm font-medium text-slate-600 mb-3">Halı {index + 1}</div>
                <div className="space-y-3">
                  <Select
                    value={carpet.carpet_type}
                    onValueChange={(value) => updateCarpet(carpet.id, "carpet_type", value)}
                  >
                    <SelectTrigger data-testid={`calc-carpet-type-${index}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(CARPET_NAMES).map(([key, name]) => (
                        <SelectItem key={key} value={key}>{name}{pricesReady && carpetPrices[key] !== undefined ? ` (${formatPrice(carpetPrices[key])}₺/m²)` : ""}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Genişlik (m)</Label>
                      <Input
                        type="number"
                        step="1"
                        min="1"
                        placeholder="2"
                        value={carpet.width}
                        onChange={(e) => updateCarpet(carpet.id, "width", e.target.value)}
                        data-testid={`calc-width-${index}`}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Uzunluk (m)</Label>
                      <Input
                        type="number"
                        step="1"
                        min="1"
                        placeholder="3"
                        value={carpet.length}
                        onChange={(e) => updateCarpet(carpet.id, "length", e.target.value)}
                        data-testid={`calc-length-${index}`}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Add Carpet Button */}
            <Button 
              variant="outline" 
              onClick={addCarpet}
              className="w-full border-dashed border-orange-300 text-orange-600 hover:bg-orange-50"
              data-testid="add-carpet-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Başka Halı Ekle
            </Button>

            {/* Price Result */}
            {priceInfo.totalPrice > 0 && (
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-4" data-testid="calc-result">
                <div className="flex items-center gap-2 mb-3">
                  <Ruler className="w-5 h-5 text-orange-500" />
                  <h3 className="font-semibold text-orange-800">Hesaplama Sonucu</h3>
                </div>
                
                {/* Details */}
                <div className="space-y-2 mb-4 text-sm">
                  {priceInfo.details.map((detail, i) => (
                    <div key={i} className="flex justify-between text-slate-600">
                      <span>{detail.type} ({detail.dimensions}m) - {formatPrice(detail.unitPrice)}₺/m²</span>
                      <span>{detail.area.toFixed(1)}m² = {detail.price.toFixed(0)}₺</span>
                    </div>
                  ))}
                </div>
                
                <div className="border-t border-orange-200 pt-3 space-y-2">
                  <div className="flex justify-between text-sm text-slate-600">
                    <span>Toplam Alan:</span>
                    <span className="font-bold">{priceInfo.totalArea.toFixed(1)} m²</span>
                  </div>
                  <div className="flex justify-between text-sm text-slate-600">
                    <span>Liste Fiyatı:</span>
                    <span className="line-through">{priceInfo.totalPrice.toFixed(0)} ₺</span>
                  </div>
                  <div className="flex justify-between text-sm text-green-600">
                    <span>İndirim (%{priceInfo.discountRate}):</span>
                    <span className="font-medium">-{priceInfo.discountAmount.toFixed(0)} ₺</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold border-t border-orange-200 pt-2">
                    <span className="text-orange-800">Ödenecek Tutar:</span>
                    <span className="text-orange-600">{priceInfo.finalPrice.toFixed(0)} ₺</span>
                  </div>
                </div>
              </div>
            )}

            {/* Warning */}
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
              <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="text-amber-800">
                <strong>Not:</strong> Bu hesaplama tahmini bir değerdir. Net fiyat, halı firmamız tarafından yapılacak inceleme sonrası belirlenecektir.
              </p>
            </div>

            <Button 
              onClick={handleGetStarted}
              className="w-full bg-orange-500 hover:bg-orange-600 py-5 rounded-xl"
              data-testid="calc-order-btn"
            >
              WhatsApp ile Talep Oluştur
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Contact Section */}
      <section id="iletisim" className="py-24 px-4 bg-gradient-to-br from-orange-50 to-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-800 mb-4">İletişime Geçin</h2>
            <p className="text-xl text-slate-600">Sorularınız için bize ulaşın, size yardımcı olmaktan mutluluk duyarız</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl shadow-lg p-8 text-center hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Phone className="w-8 h-8 text-orange-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-2">Telefon</h3>
              <p className="text-slate-600 mb-4">Bizi arayın</p>
              <a href="tel:0212 699 03 01" className="text-orange-600 hover:text-orange-700 font-semibold">
                0212 699 03 01
              </a>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8 text-center hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-orange-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-2">E-posta</h3>
              <p className="text-slate-600 mb-4">Bize yazın</p>
              <a href="mailto:info@haliyol.com" className="text-orange-600 hover:text-orange-700 font-semibold">
                info@haliyol.com
              </a>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8 text-center hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <MapPin className="w-8 h-8 text-orange-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-2">Adres</h3>
              <p className="text-slate-600 mb-4">Merkez Ofis</p>
              <p className="text-slate-700 font-medium">
                İstanbul, Türkiye
              </p>
            </div>
          </div>

          <div className="mt-12 text-center">
            <Link to="/support">
              <Button className="bg-orange-500 hover:bg-orange-600 text-white rounded-full px-8 py-6 text-lg font-semibold">
                Yardım Merkezi
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* WhatsApp Floating Button - desktop only, mobile uses bottom bar */}
      <button
        onClick={handleWhatsAppClick}
        className="hidden md:flex fixed bottom-6 right-6 z-50 bg-green-500 hover:bg-green-600 text-white rounded-full p-4 shadow-2xl transition-all duration-300 hover:scale-110 group"
        aria-label="WhatsApp ile iletişime geç"
      >
        <MessageCircle className="w-7 h-7" />
        <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-slate-800 text-white px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
          WhatsApp ile talep oluştur
        </span>
      </button>

      {/* Mobile Bottom Action Bar */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-orange-100 shadow-[0_-4px_20px_rgba(0,0,0,0.08)] grid grid-cols-3">
        <a
          href="tel:02126990301"
          className="flex flex-col items-center justify-center gap-1 py-2.5 text-slate-700 active:bg-orange-50 transition-colors"
          aria-label="Hemen Ara"
        >
          <Phone className="w-5 h-5 text-orange-500" />
          <span className="text-xs font-medium">Ara</span>
        </a>
        <button
          onClick={handleWhatsAppClick}
          className="flex flex-col items-center justify-center gap-1 py-2.5 bg-green-500 text-white active:bg-green-600 transition-colors"
          aria-label="WhatsApp ile iletişime geç"
        >
          <MessageCircle className="w-5 h-5" />
          <span className="text-xs font-medium">WhatsApp</span>
        </button>
        <a
          href="#hizmetler"
          className="flex flex-col items-center justify-center gap-1 py-2.5 text-slate-700 active:bg-orange-50 transition-colors"
          aria-label="Fiyat Listesi"
        >
          <Calculator className="w-5 h-5 text-orange-500" />
          <span className="text-xs font-medium">Fiyat</span>
        </a>
      </div>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <CarpetLogo className="w-8 h-8" />
                <span className="text-xl font-bold">HALIYOL</span>
              </div>
              <p className="text-slate-400">
                Halılarınız için en iyi yıkama hizmeti
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Hızlı Linkler</h4>
              <div className="space-y-2">
                <Link to="/" className="block text-slate-400 hover:text-white transition-colors">Ana Sayfa</Link>
                <Link to="/about" className="block text-slate-400 hover:text-white transition-colors">Hakkımızda</Link>
                <Link to="/blog" className="block text-slate-400 hover:text-white transition-colors">Blog</Link>
                <Link to="/support" className="block text-slate-400 hover:text-white transition-colors">Yardım & Destek</Link>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Yasal</h4>
              <div className="space-y-2">
                <Link to="/support?section=kvkk" className="block text-slate-400 hover:text-white transition-colors">KVKK</Link>
                <Link to="/support?section=privacy" className="block text-slate-400 hover:text-white transition-colors">Gizlilik Politikası</Link>
                <Link to="/support?section=cookies" className="block text-slate-400 hover:text-white transition-colors">Çerez Politikası</Link>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-4">İletişim</h4>
              <div className="space-y-2 text-slate-400">
                <p className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  info@haliyol.com
                </p>
                <p className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  0212 699 03 01
                </p>
              </div>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 text-center text-slate-400">
            <p>&copy; 2026 HALIYOL. Tüm hakları saklıdır.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
